import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ConnectorFieldStatus.module.css';

type ConnectorFieldStatusVariant = 'configured' | 'enabled';

type ConnectorFieldStatusProps = {
  done: boolean;
  variant?: ConnectorFieldStatusVariant;
};

const parseSubscribedFields = (value: string): string[] => (
  value
    .split(',')
    .map(field => field.trim())
    .filter(Boolean)
);

export const ConnectorFieldStatus: React.FC<ConnectorFieldStatusProps> = ({
  done,
  variant = 'configured',
}) => {
  const { t } = useTranslation('companies');
  const doneLabel = variant === 'enabled' ? t('enabled') : t('configured');
  const notDoneLabel = variant === 'enabled' ? t('disabled') : t('missing');

  return (
    <span className={styles.fieldStatus}>
      {done ? (
        <>
          <span className={styles.checkIcon} aria-hidden="true">✓</span>
          <span>{doneLabel}</span>
        </>
      ) : (
        <>
          <span className={styles.pendingIcon} aria-hidden="true">–</span>
          <span className={styles.notDoneLabel}>{notDoneLabel}</span>
        </>
      )}
    </span>
  );
};

type ConnectorSubscribedFieldsProps = {
  fields: string[] | string;
};

export const ConnectorDoneLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className={styles.fieldStatus}>
    <span className={styles.checkIcon} aria-hidden="true">✓</span>
    <span>{children}</span>
  </span>
);

export const ConnectorSubscribedFields: React.FC<ConnectorSubscribedFieldsProps> = ({ fields }) => {
  const normalizedFields = Array.isArray(fields) ? fields : parseSubscribedFields(fields);

  if (normalizedFields.length === 0) {
    return null;
  }

  return (
    <ul className={styles.subscribedFields}>
      {normalizedFields.map(field => (
        <li key={field} className={styles.subscribedFieldItem}>
          <span className={styles.subscribedFieldCheck} aria-hidden="true">✓</span>
          <span>{field}</span>
        </li>
      ))}
    </ul>
  );
};
